/**
 * useDragAutoScroll — scrolls the page (or the nearest scrollable ancestor)
 * while an HTML5 drag rides near the viewport's top/bottom edge, faster the
 * closer to the edge. HTML5 drag does NOT scroll on its own, so without this a
 * 40-row builder list can only be reordered a screenful at a time (lift, drop,
 * scroll, lift again). Extracted from FormBuilder; shared with the prescreen
 * builder.
 */
import { RefObject, useEffect } from 'react';

/** Nearest ancestor that actually scrolls, or `window`. The builder can be
 * rendered inside a scrolling shell or against the document — drag auto-scroll
 * has to move whichever one is real. */
function scrollHost(el: HTMLElement | null): HTMLElement | Window {
  for (let n = el?.parentElement || null; n; n = n.parentElement) {
    const { overflowY } = window.getComputedStyle(n);
    if ((overflowY === 'auto' || overflowY === 'scroll') && n.scrollHeight > n.clientHeight) return n;
  }
  return window;
}

// Keyed on "is a drag in flight", NOT the dragged index: the index changes on
// every dragover step, and re-creating the loop each step would reset the
// scroll velocity mid-drag.
export default function useDragAutoScroll(dragging: boolean, listRef: RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (!dragging) return undefined;
    const host = scrollHost(listRef.current);
    const EDGE = 100;       // px from the edge where scrolling kicks in
    const MAX_SPEED = 24;   // px per frame at the very edge
    let velocity = 0;
    let raf = 0;
    const onWindowDragOver = (e: DragEvent) => {
      const top = host === window ? 0 : (host as HTMLElement).getBoundingClientRect().top;
      const bottom = host === window ? window.innerHeight : (host as HTMLElement).getBoundingClientRect().bottom;
      if (e.clientY < top + EDGE) velocity = -MAX_SPEED * Math.min(1, (top + EDGE - e.clientY) / EDGE);
      else if (e.clientY > bottom - EDGE) velocity = MAX_SPEED * Math.min(1, (e.clientY - (bottom - EDGE)) / EDGE);
      else velocity = 0;
    };
    const tick = () => {
      if (velocity !== 0) host.scrollBy(0, velocity);
      raf = window.requestAnimationFrame(tick);
    };
    window.addEventListener('dragover', onWindowDragOver);
    raf = window.requestAnimationFrame(tick);
    return () => {
      window.removeEventListener('dragover', onWindowDragOver);
      window.cancelAnimationFrame(raf);
    };
  }, [dragging, listRef]);
}
